import Button from "@mui/material/Button";
import { styled, Theme, useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import RowLink from "_shared/_views/tables/row-link";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { useGetIndividualsByEntityIdQuery } from "marketplace/individuals/_api/individual-api";
import { getIndividualsByEntity } from "marketplace/individuals/_stores/individuals-redux-slice";
import Individual from "marketplace/individuals/individual";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import {
  createMRTColumnHelper,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
  MRT_TableInstance,
  MRT_TableOptions,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
} from "material-react-table";
import {
  searchFieldSpacing,
  searchTextFieldProps,
  topToolbarFitsThePage,
} from "_shared/_views/tables/table-toolbar";

import { ToolbarButtons, ToolbarButtonsContainer } from "permissions/_views/tables/permissions-table-styles";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import * as React from "react";
import { ReactNode, useCallback } from "react";
import { useSelector } from "react-redux";
import { Link as RouterLink, useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";

const NameCellContainer = styled(Box)(() => ({
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
}));
const NameText = styled(ListItemText)(({ theme }) => ({
  marginLeft: theme.spacing(1),
}));
const RowTitle = styled(Typography)(() => ({
  fontWeight: "500",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

type EntityMembersTableInputProps = {
  canInviteMembers: boolean;
  canManageMemberAccess: boolean;
  renderTopToolbarCustomActions: (props: { table: MRT_TableInstance<Individual> }) => ReactNode;
};

const membersColumnHelper = createMRTColumnHelper<Individual>();

export default function EntityMembersTable(props: Readonly<EntityMembersTableInputProps>) {
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const { canInviteMembers, canManageMemberAccess, renderTopToolbarCustomActions } = props;

  // State
  const [query, setQuery] = React.useState("");
  const [columnFilters, setColumnFilters] = React.useState<MRT_ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<MRT_SortingState>([]);
  const [pagination, setPagination] = React.useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  // Hooks
  const navigate = useNavigate();
  const session = useSession();
  const { openDialog } = useDialog();

  // Queries
  const individuals = useSelector(getIndividualsByEntity(session.user!.legalEntityId));
  const { isFetching, isLoading, isError: hasError, refetch } = useGetIndividualsByEntityIdQuery({
    legalEntityId: session.user!.legalEntityId.value,
  });

  const handleViewProfile = useCallback(
    (individualId?: Guid): void => {
      if (!individualId) return;

      const parentPath = new URL(".", globalThis.location.origin + globalThis.location.pathname);

      openDialog({
        titleStyle: {
          paddingBottom: 0,
        },
        contentSxProps: {
          display: "flex",
          overflowX: "hidden",
        },
        MuiProps: {
          maxWidth: "lg",
          fullWidth: false,
        },
        component: <ViewIndividualProfile individualId={individualId} />,
        onClose: () => {
          navigate(parentPath.pathname);
        },
      });
    },
    [openDialog, navigate],
  );

  const renderToolbarInternalActions = useCallback(
    (props: { table: MRT_TableInstance<Individual> }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <GlobalFilterTextField table={props.table} sx={searchFieldSpacing} />
              <ToggleFiltersButton table={props.table} />
              <ShowHideColumnsButton table={props.table} />
              <ToggleFullScreenButton table={props.table} />
            </ToolbarButtons>
            {canInviteMembers && (
              <Button startIcon={<PersonAddIcon />} onClick={() => navigate(`/organization/invitations/invite`)}>
                Invite Users
              </Button>
            )}
          </ToolbarButtonsContainer>
        )}
      </>
    ),
    [canInviteMembers, navigate, isMediumDisplaySize],
  );

  const renderNameColumn = useCallback(
    (individual: Individual): ReactNode => {
      if (!individual) return <></>;

      let name = `${individual.profile.firstName} ${individual.profile.lastName}`;
      let email = individual.profile.email?.value;

      // A button rather than an anchor: the profile opens in a dialog over this page and there is no
      // URL to point at. The cell around it keeps its own click, so the avatar and the email still
      // open the profile too.
      const contactLink = (
        <RowTitle>
          <RowLink onClick={() => handleViewProfile(individual.id)}>{name}</RowLink>
        </RowTitle>
      );

      return (
        <NameCellContainer onClick={() => handleViewProfile(individual.id)}>
          <IndividualAvatar individual={individual} />
          <NameText primary={contactLink} secondary={email} />
        </NameCellContainer>
      );
    },
    [handleViewProfile],
  );

  const renderRoleColumn = useCallback((individual: Individual) => {
    if (individual.hasSigningAuthority) return "Signing Authority";
    return "Member";
  }, []);

  const renderPermissionsColumn = useCallback((data: Individual) => {
    return (
      <Link
        component={RouterLink}
        to={{
          pathname: "/permissions",
          search: `?granteeUserId=${data.userId}`,
        }}
      >
        Manage access
      </Link>
    );
  }, []);

  const columns = React.useMemo(
    () => [
      membersColumnHelper.display({
        header: "Name",
        id: "name",
        Cell: ({ row }) => renderNameColumn(row.original),
      }),
      membersColumnHelper.display({
        header: "Role",
        id: "isAdmin",
        Cell: ({ row }) => renderRoleColumn(row.original),
      }),
      ...(canManageMemberAccess
        ? [
            membersColumnHelper.display({
              header: "Access",
              id: "permissions",
              Cell: ({ row }) => renderPermissionsColumn(row.original),
            }),
          ]
        : []),
    ],
    [canManageMemberAccess, renderNameColumn, renderRoleColumn, renderPermissionsColumn],
  );

  const tableOptions: MRT_TableOptions<Individual> = {
    columns,
    data: individuals ?? [],
    enableRowSelection: false,
    enableTableHead: true,
    enableRowActions: false,
    enableColumnPinning: true,
    initialState: {
      showGlobalFilter: true,
      columnPinning: { right: ["mrt-row-actions"] },
    },
    positionGlobalFilter: "none",
    muiSearchTextFieldProps: ({ table }) =>
      searchTextFieldProps({ searches: "members", table }),
    renderEmptyRowsFallback: emptyRowsFallback({
      isError: hasError,
      isLoading: isFetching,
      onRetry: () => void refetch(),
      subject: "members",
    }),
    getRowId: (row) => row.id?.value ?? Guid.generate().value,
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnFilters: true,
    manualPagination: true,
    manualSorting: true,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setQuery,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    renderTopToolbarCustomActions: renderTopToolbarCustomActions,
    renderToolbarInternalActions: renderToolbarInternalActions,
    muiTopToolbarProps: { sx: topToolbarFitsThePage },
    muiTableContainerProps: {
      sx: {
        flex: 1,
      },
    },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
      },
    },
    muiToolbarAlertBannerProps: hasError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    rowCount: individuals?.length ?? 0,
    state: {
      columnFilters,
      globalFilter: query,
      isLoading,
      pagination,
      showAlertBanner: !!hasError,
      showProgressBars: isLoading,
      sorting,
    },
  };

  return <MRTNoMemo<Individual> options={tableOptions} />;
}
